import { Avatar } from '@components/avatar';
import { IconApplicants } from '@components/icons/applicants';
import { IconApplicantsSmall } from '@components/icons/applicants-small';
import { IconArchiveSmall } from '@components/icons/archive-small';
import { IconAssignSmall } from '@components/icons/assign-small';
import { IconCheckmarkSmall } from '@components/icons/checkmark-small';
import { IconDateSmall } from '@components/icons/date-small';
import { IconHireSmall } from '@components/icons/hire-small';
import { IconJob } from '@components/icons/job';
import { IconMessagesSmall } from '@components/icons/messages-small';
import { IconMoveSmall } from '@components/icons/move-small';
import { IconNotesSmall } from '@components/icons/notes-small';
import { IconOnboarding } from '@components/icons/onboarding';
import { IconPersonLimitedSmall } from '@components/icons/person-limited-small';
import { IconPublishSmall } from '@components/icons/publish-small';
import { IconRatingSmall } from '@components/icons/rating-small';
import { IconRejectSmall } from '@components/icons/reject-small';
import { IconTeamSmall } from '@components/icons/team-small';

import { Notification } from 'api/notifications/requests';
import { useAuthenticatedUser } from 'features/authentication';
import {
  applicationBaseUrl,
  applicationMessagesUrl,
  applicationNotesUrl,
  applicationOnboardingAppUrl,
  applicationRatingsUrl,
  jobNotesUrl,
  jobPipelineUrl,
} from 'features/routing';
import { useStore } from 'models/Provider';
import { IUser } from 'models/types';
import { NotificationType } from 'types/notifications';

export const getUrlForNotification = (notification: Notification) => {
  const { notificationType, applicationId, jobId, triggerId } = notification;
  switch (notificationType) {
    case 'ADD_RATING':
      return applicationRatingsUrl(applicationId, jobId);
    case 'ADD_NOTE':
    case 'ADD_NOTE_MENTION':
    case 'UPDATE_NOTE_MENTION':
      return applicationNotesUrl(applicationId, jobId);
    case 'INTEGRATION':
      return applicationOnboardingAppUrl(applicationId, jobId, triggerId);
    case 'APPLICATION_MESSAGE':
      return applicationMessagesUrl(applicationId, jobId);
    case 'EXPIRE_JOB':
    case 'ARCHIVE_JOB':
    case 'ADD_TO_HIRING_TEAM':
      return jobPipelineUrl(jobId);
    case 'ADD_JOB_NOTE':
    case 'ADD_JOB_NOTE_MENTION':
    case 'UPDATE_JOB_NOTE_MENTION':
      return jobNotesUrl(jobId);
    default:
      return applicationBaseUrl(applicationId, jobId);
  }
};

export const getIconForAction = (notificationType: NotificationType) => {
  switch (notificationType) {
    case 'APPLY_JOB':
      return <IconApplicantsSmall color="purple50" />;
    case 'APPLICANT_HIRED':
      return <IconHireSmall />;
    case 'INTEGRATION':
      return <IconCheckmarkSmall color="green100" />;
    case 'BULK_REJECT_APPLICANTS':
    case 'APPLICANT_REJECTED':
      return <IconRejectSmall color="red100" />;
    case 'ADD_NOTE':
    case 'ADD_NOTE_MENTION':
    case 'UPDATE_NOTE_MENTION':
    case 'ADD_JOB_NOTE':
    case 'ADD_JOB_NOTE_MENTION':
    case 'UPDATE_JOB_NOTE_MENTION':
      return <IconNotesSmall color="purple50" />;
    case 'ADD_RATING':
      return <IconRatingSmall color="purple50" />;
    case 'ADD_TO_APPLICATION':
      return <IconPersonLimitedSmall color="purple50" />;
    case 'ADD_TO_HIRING_TEAM':
      return <IconTeamSmall color="purple50" />;
    case 'EXPIRE_JOB':
      return <IconDateSmall color="purple50" />;
    case 'PUBLISH_JOB':
      return <IconPublishSmall color="purple50" />;
    case 'ARCHIVE_JOB':
      return <IconArchiveSmall color="purple50" />;
    case 'MOVE_STAGE':
      return <IconMoveSmall color="purple50" />;
    case 'APPLICANT_ASSIGNED':
      return <IconAssignSmall color="purple50" />;
    case 'APPLICATION_MESSAGE':
    case 'APPLICATION_ASSIGNED_MESSAGE':
      return <IconMessagesSmall color="purple50" />;
  }
};

export const getIconForNotification = (notificationType: NotificationType, user: IUser) => {
  switch (notificationType) {
    case 'APPLY_JOB':
    case 'APPLICATION_MESSAGE':
    case 'APPLICATION_ASSIGNED_MESSAGE':
      return <IconApplicants size="40" color="purple50" />;
    case 'INTEGRATION':
      return <IconOnboarding size="40" color="purple50" />;
    case 'EXPIRE_JOB':
      return <IconJob size="40" color="purple50" />;
    default:
      return <Avatar size="lg" name={user?.name} src={user?.picture} />;
  }
};

type ContentProps = {
  afterText?: string;
  applicationIds: number[];
  beforeText?: string;
  objectId: number | null;
  triggerType: string | null;
  userId: number | null;
  applicationName: string;
  notificationType: NotificationType;
};

export const NotificationContent = (props: ContentProps) => {
  const {
    afterText,
    applicationIds,
    applicationName,
    beforeText,
    notificationType,
    objectId,
    triggerType,
    userId,
  } = props;
  const { t, userStore } = useStore();
  const me = useAuthenticatedUser();
  const user =
    userId !== null ? (
      userId === me.id ? (
        t('notifications.single.you')
      ) : (
        userStore.items.get(userId).name
      )
    ) : (
      <></>
    );
  switch (notificationType) {
    case 'APPLICATION_MESSAGE':
    case 'APPLICATION_ASSIGNED_MESSAGE':
      return (
        <>
          {t('notifications.single.message')} <strong>{applicationName}</strong>
        </>
      );
    case 'APPLICANT_HIRED':
      return (
        <>
          {user} {t('notifications.single.applicant_hired')} <strong>{applicationName} </strong>
          {t('notifications.single.applicant_as_hired')}
        </>
      );
    case 'APPLICANT_REJECTED':
      return (
        <>
          {user} {t('notifications.single.applicant_rejected')} <strong>{applicationName} </strong>
          {t('notifications.single.applicant_rejected_as')}
        </>
      );
    case 'APPLICANT_ASSIGNED':
      return (
        <>
          {user} {t('notifications.single.applicant_assigned')} <strong>{applicationName}</strong>
          {t('notifications.single.applicant_assigned_to')}
        </>
      );
    case 'MOVE_STAGE':
      return (
        <>
          {user} {t('notifications.single.move_stage')} <strong>{applicationName} </strong>
          {t('notifications.single.move_stage_from')} <strong>{beforeText} </strong>
          {t('notifications.single.move_stage_to')} <strong>{afterText}</strong>
        </>
      );
    case 'ARCHIVE_JOB':
      return (
        <>
          <strong>{user} </strong>
          {t('notifications.single.job_archived')}
        </>
      );
    case 'PUBLISH_JOB':
      return (
        <>
          {user} {t('notifications.single.job_published')}
        </>
      );
    case 'EXPIRE_JOB':
      return <>{t('notifications.single.job_expired')}</>;
    case 'ADD_JOB_NOTE':
      return (
        <>
          {user} {t('notifications.single.add_job_note')} <strong> {applicationName} </strong>
        </>
      );
    case 'ADD_JOB_NOTE_MENTION':
    case 'UPDATE_JOB_NOTE_MENTION':
      return (
        <>
          {user} {t('notifications.single.add_job_note_mention')}
        </>
      );
    case 'ADD_TO_HIRING_TEAM':
      const affectedUser = userStore.items.get(objectId);
      if (!affectedUser) {
        return <></>;
      }
      return (
        <>
          {user} {t('notifications.single.added')}{' '}
          <strong>
            {affectedUser.id === me.id ? t('notifications.single.you') : affectedUser.name}{' '}
          </strong>
          {t('notifications.single.to_hiring_team')}
        </>
      );
    case 'APPLY_JOB':
      return (
        <>
          {t('notifications.single.new_applications')} <strong> {applicationName} </strong>
        </>
      );
    case 'ADD_RATING':
      return (
        <>
          {user} {t('notifications.single.add_rating')} <strong> {applicationName} </strong>
        </>
      );
    case 'ADD_NOTE':
      return (
        <>
          {user} {t('notifications.single.add_note')} <strong> {applicationName} </strong>
        </>
      );
    case 'ADD_NOTE_MENTION':
    case 'UPDATE_NOTE_MENTION':
      return (
        <>
          {user} {t('notifications.single.add_note_mention')} <strong> {applicationName} </strong>
        </>
      );
    case 'BULK_REJECT_APPLICANTS':
      return (
        <>
          {user} {t('notifications.single.bulk_reject')}
          <strong>
            {' '}
            {applicationIds.length} {t('notifications.single.bulk_reject_applicants')}
          </strong>
        </>
      );
    case 'ADD_TO_APPLICATION':
      return (
        <>
          {user} {t('notifications.addtoapplication')}
          <strong>{applicationName}</strong>
        </>
      );
    case 'INTEGRATION':
      switch (triggerType) {
        case 'ONBOARDING_FORM_SEND':
          return (
            <>
              {user} {t('notifications.integrations.onboarding_form.send')}
              <strong> {applicationName}</strong>
            </>
          );
        case 'ONBOARDING_FORM_FINISHED':
          return (
            <>
              {t('notifications.integrations.onboarding_form.finished_new')}
              <strong> {applicationName} </strong>
              {t('notifications.integrations.onboarding_form.finished_has')}
            </>
          );
        case 'SIGNET_CONTRACT_SIGNED':
          return (
            <>
              {t('notifications.integrations.signet.finished_new')}
              <strong> {applicationName} </strong>
              {t('notifications.integrations.signet.finished_has')}
            </>
          );
        default:
          return <></>;
      }
    default:
      return <></>;
  }
};
